import { ChangeEvent, FC, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Chip,
  TextField,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Drawer,
  IconButton,
  Divider,
} from '@mui/material';
import {
  DesktopTower,
  FileText,
  Globe,
  ForkKnife,
  X,
  CheckCircle,
} from '@phosphor-icons/react';
import { RootState } from '../../utils/redux/redux';
import {
  updateMenuConfig,
  setStepStatus,
  setErrors,
  clearErrors,
  setIsAccessRequested,
  clearAllState,
  setIsMenuGenerationStarted,
  setHasUnsavedChanges,
} from '../../utils/redux/slices/userJourneySlice';
import { menuConfigurationSchema } from '../../schemas/userJourney/menuConfiguration';
import { ValidationError } from 'yup';
import AccessRequestSuccessModal from '../modals/RequestAccessModal';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  connectBusinessApi,
  getPosConfigApi,
  getTmBillBrandInfoApi,
  getTmBillMenuApi,
} from '../../utils/apis';
import { useSaveAssistant } from '../../hooks/useSaveAssistant';
import { useUser } from '../../hooks/useUser';
import { useAgents } from '../../hooks/useAgents';
import TMBillBrandInfoModal from '../modals/TMBillBrandInfoModal';
import { useAlert } from '../../hooks/useAlert';
import MessagePopup from '../common/MessagePopup';
import ConfirmationModal from '../modals/CofirmationModal';
import { usePosIntegrationData } from '../../hooks/usePosIntegrationData';
interface MenuConfigurationDrawerProps {
  open: boolean;
  onClose: () => void;
  store: any;
  fromStoreDetails?: boolean;
}

const MenuConfigurationDrawer: FC<MenuConfigurationDrawerProps> = ({
  open,
  onClose,
  store,
}) => {
  const dispatch = useDispatch();
  const { menuConfig, errors } = useSelector(
    (state: RootState) => state.userJourney as { menuConfig: any; errors: any }
  );

  const { data: posIntegrationData } = usePosIntegrationData();
  const queryClient = useQueryClient();

  // Find the matching integration data for this store
  const storeIntegration = posIntegrationData?.data?.data?.find(
    (integration: any) => integration.storeId === store.id
  );

  const [isEditing, setIsEditing] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [showBrandInfoModal, setShowBrandInfoModal] = useState(false);
  const [isBrandInfoLoading, setIsBrandInfoLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedStoreInfo, setSelectedStoreInfo] = useState({
    outletName: '',
    brandName: '',
    address: '',
    tmBillBrandShortName: '',
    storeId: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { saveAssistant, isPending: isSaving } = useSaveAssistant();
  const { user } = useUser();
  const { agents } = useAgents();
  const { alert, setAlert, showAlert } = useAlert();

  const methods = [
    {
      id: 'pos',
      title: 'POS Integration',
      description: 'Sync with your existing system for real-time updates',
      icon: <DesktopTower size={24} weight="duotone" />,
      highlight: (
        <Chip
          label="Fastest"
          size="small"
          sx={{
            '& .MuiChip-label': {
              fontSize: '10px',
              color: 'custom.textBlue',
              fontWeight: 'bold',
            },
          }}
        />
      ),
    },
    {
      id: 'file',
      title: 'File Upload',
      description: 'Upload a CSV or spreadsheet to import your menu',
      icon: <FileText size={24} weight="duotone" />,
    },
    {
      id: 'web',
      title: 'Web Scrapping',
      description: 'Fetch menu details directly from your website',
      icon: <Globe size={24} weight="duotone" />,
    },
    {
      id: 'manual',
      title: 'Manual Cataloging',
      description: 'Enter items manually for full control over menu items',
      icon: <ForkKnife size={24} weight="duotone" />,
    },
  ];

  const getMethodId = (method: string) => {
    switch (method) {
      case 'pos_integration':
        return 'pos';
      case 'file_upload':
        return 'file';
      case 'web_scraping':
        return 'web';
      case 'manual':
        return 'manual';
      default:
        return '';
    }
  };

  const { data: posConfig } = useQuery({
    queryKey: ['pos-config'],
    queryFn: getPosConfigApi,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
  });

  const getEnvironmentFromApiKey = (
    currentApiKey: string,
    posName: string
  ): string => {
    if (!currentApiKey || !posConfig?.data?.data) return 'stage';

    const selectedPos = posConfig.data.data.find(
      (pos: any) => pos.posName === posName
    );

    if (!selectedPos?.pos_config?.[0]) return 'stage';

    // Loop through environments to find matching API key
    for (const [environment, configs] of Object.entries(
      selectedPos.pos_config[0]
    )) {
      const envConfig = (configs as any)[0];
      if (envConfig?.apikey === currentApiKey) {
        return environment;
      }
    }

    return 'stage';
  };

  useEffect(() => {
    if (open) {
      // Clear the previous state whenever the drawer opens
      dispatch(clearAllState());

      // Only set data if this store has configuration
      if (storeIntegration && store.is_menu_configured !== false) {
        // Determine environment by matching API key
        const detectedEnvironment = getEnvironmentFromApiKey(
          storeIntegration?.pos_config?.apikey,
          storeIntegration?.posName
        );

        const menuConfigData = {
          menuSettingMethod: getMethodId(storeIntegration?.menuSettingMethod),
          posName: storeIntegration?.posName || '',
          businessId: storeIntegration?.businessId || '',
          tmbillposId: storeIntegration?.tmbillposId || '',
          environment: detectedEnvironment, // Use detected environment
          posConfig: {
            apikey: storeIntegration?.pos_config?.apikey || '',
            endpoint: storeIntegration?.pos_config?.endpoint || '',
          },
          menuSettingDetails: {
            [store.id]: {
              storeId: store.id,
              refId: storeIntegration?.locationRefId || '',
              tmBillBrandShortName:
                storeIntegration?.tmBillBrandShortName || '',
            },
          },
        };

        // Dispatch the data to Redux
        dispatch(updateMenuConfig(menuConfigData));

        // Only set these flags if they exist in storeIntegration
        dispatch(setIsAccessRequested(true));
        dispatch(setIsMenuGenerationStarted(true));
      }

      dispatch(setHasUnsavedChanges(false));
    }
  }, [open, store.id, storeIntegration, store.is_menu_configured, posConfig]);

  const handleMethodSelect = (method: string) => {
    dispatch(updateMenuConfig({ menuSettingMethod: method }));
    dispatch(setHasUnsavedChanges(true));
    if (errors.menuSettingMethod) {
      dispatch(setErrors({ ...errors, menuSettingMethod: '' }));
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // If businessId changes, reset the access request status
    if (name === 'businessId') {
      dispatch(setIsAccessRequested(false));
    }

    // Update the Redux state with the input value
    dispatch(updateMenuConfig({ [name]: value }));
    dispatch(setHasUnsavedChanges(true));

    // If environment changes, update the posConfig data in Redux
    if (name === 'environment') {
      const selectedPos = posConfig?.data?.data?.find(
        (pos: any) => pos.posName === menuConfig.data.posName
      );

      if (selectedPos?.pos_config?.[0]?.[value]?.[0]) {
        const envConfig = selectedPos.pos_config[0][value][0];

        // Update Redux with the apikey and endpoint for the selected environment
        dispatch(
          updateMenuConfig({
            posConfig: {
              apikey: envConfig.apikey,
              endpoint: envConfig.endpoint,
            },
          })
        );
      }
    }

    // If posName changes, reset environment-specific data and select the first available environment
    if (name === 'posName') {
      const selectedPos = posConfig?.data?.data?.find(
        (pos: any) => pos.posName === value
      );

      if (selectedPos?.pos_config?.[0]) {
        const environments = Object.keys(selectedPos.pos_config[0]);
        // Only set default environment if no environment is currently selected
        const currentEnv = menuConfig.data.environment;
        const defaultEnv = environments.includes(currentEnv)
          ? currentEnv
          : environments[0] || 'stage';
        const envConfig = selectedPos.pos_config[0][defaultEnv][0];

        // Update Redux with the default environment and its config
        dispatch(
          updateMenuConfig({
            environment: defaultEnv,
            posConfig: {
              apikey: envConfig.apikey,
              endpoint: envConfig.endpoint,
            },
          })
        );
      }
    }

    if (errors[name]) {
      dispatch(setErrors({ ...errors, [name]: '' }));
    }
  };

  const handleCloseAccessModal = () => {
    setShowAccessModal(false);
  };

  const handleCloseDrawer = () => {
    // Only show confirmation modal for TMBill POS type when changes exist
    if (menuConfig.hasUnsavedChanges && menuConfig.data.posName === 'tmbill') {
      setShowConfirmModal(true);
    } else {
      onClose();
    }
  };

  const handlePreviewInfo = async () => {
    const brandShortName =
      menuConfig.data.menuSettingDetails?.[store.id]?.tmBillBrandShortName;
    if (brandShortName) {
      try {
        setIsBrandInfoLoading(true);
        setShowBrandInfoModal(true);

        const response = await getTmBillBrandInfoApi(
          user?.tenantName,
          brandShortName
        );

        // Check if response.data has the expected structure
        if (response.data) {
          const brandData = response.data;
          // Get the first outlet data (assuming there's at least one)
          const outletData =
            brandData.outlets && brandData.outlets.length > 0
              ? brandData.outlets[0]
              : { name: '', address: '' };

          setSelectedStoreInfo({
            outletName: outletData.name || ' ',
            brandName: brandData.brand_name || ' ',
            address: outletData.address || ' ',
            tmBillBrandShortName: brandData.brand_short_name || '',
            storeId: store.id || '',
          });
        }
      } catch (error) {
        console.error('Error fetching brand info:', error);
      } finally {
        setIsBrandInfoLoading(false);
      }
    }
  };

  const handleMenuGeneration = () => {
    try {
      setIsSubmitting(true);

      // Start the API call but don't wait for its completion
      getTmBillMenuApi(
        user?.tenantName,
        selectedStoreInfo.tmBillBrandShortName,
        selectedStoreInfo.storeId
      )
        .then(response => {
          console.log(response);
        })
        .catch(error => {
          console.error('Error generating TM Bill menu:', error);
        });

      // Close the modal and show success message after 3 seconds
      setTimeout(() => {
        setShowBrandInfoModal(false);
        setIsSubmitting(false);
        dispatch(setIsMenuGenerationStarted(true));
        showAlert(
          'Your menu will show up in 2-3 minutes in knowledge base',
          'success'
        );
      }, 3000);
    } catch (error) {
      console.error('Error starting TM Bill menu generation:', error);
      showAlert('Failed to generate TM Bill menu. Please try again.', 'error');
      setIsSubmitting(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);

      await menuConfigurationSchema.validate(menuConfig.data, {
        abortEarly: false,
      });

      // Format the data but don't dispatch updates yet
      let menuSettingDetails;
      if (menuConfig.data.posName === 'Urban Piper') {
        menuSettingDetails = [
          {
            storeId: store.id,
            refId: menuConfig.data.menuSettingDetails?.[store.id]?.refId || '',
          },
        ];
      } else if (menuConfig.data.posName === 'tmbill') {
        menuSettingDetails = [
          {
            storeId: store.id,
            tmBillBrandShortName:
              menuConfig.data.menuSettingDetails?.[store.id]
                ?.tmBillBrandShortName || '',
          },
        ];
      } else {
        menuSettingDetails = [
          {
            storeId: store.id,
            refId: '',
          },
        ];
      }

      const formattedMenuConfig = {
        menuSettingMethod:
          menuConfig.data.menuSettingMethod === 'pos'
            ? 'pos_integration'
            : menuConfig.data.menuSettingMethod === 'web'
              ? 'web_scrapping'
              : menuConfig.data.menuSettingMethod === 'file'
                ? 'file_upload'
                : 'manual_cataloging',
        menuSettingDetails: menuSettingDetails,
        posName: menuConfig.data.posName,
        ...(menuConfig.data.posName === 'Urban Piper' && {
          businessId: menuConfig.data.businessId,
          environment: menuConfig.data.environment,
        }),
        ...(menuConfig.data.posName === 'tmbill' && {
          tmbillposId: menuConfig.data.tmbillposId,
        }),
      };

      // Wait for API call to complete before updating state
      await saveAssistant({
        userId: user?._id,
        assistantId: agents?.agents[agents.agents.length - 1]._id,
        businessId: user?.business?._id,
        status: 'complete',
        section: 'menu-configuration',
      })
        .then(() => {
          showAlert('Menu configuration saved successfully', 'success');
        })
        .finally(() => {
          queryClient.invalidateQueries({ queryKey: ['pos-integration-data'] });
          setIsEditing(false);
          handleCloseDrawer();
        });
      // Only after API succeeds, update Redux state
      dispatch(updateMenuConfig(formattedMenuConfig));
      dispatch(
        setStepStatus({
          step: 'menuConfig',
          status: { isComplete: true, hasError: false },
        })
      );
      dispatch(clearErrors());
      dispatch(setHasUnsavedChanges(false));

      // Close confirmation modal if open
      setShowConfirmModal(false);

      // Close drawer first
      onClose();

      // Clear state after drawer is closed
      setTimeout(() => {
        dispatch(clearAllState());
      }, 100);
    } catch (err) {
      if (err instanceof ValidationError) {
        const newErrors: Record<string, string> = {};
        err.inner.forEach((error: any) => {
          newErrors[error.path as string] = error.message;
        });
        dispatch(setErrors(newErrors));
        dispatch(
          setStepStatus({
            step: 'menuConfig',
            status: { isComplete: false, hasError: true },
          })
        );
      } else {
        // Handle API errors
        showAlert(
          'Failed to save menu configuration. Please try again.',
          'error'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const { mutateAsync: connectBusiness, isPending: isConnectingBusiness } =
    useMutation({
      mutationFn: (businessId: string) => connectBusinessApi(businessId),
      onSuccess: () => {
        dispatch(setIsAccessRequested(true));
        setShowAccessModal(true);
      },
      onError: () => {
        console.log('Business connection failed');
      },
    });

  const handleRequestAccess = () => {
    connectBusiness(menuConfig.data.businessId);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleCloseDrawer}
        slotProps={{
          paper: {
            sx: {
              width: '500px',
              p: 3,
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            },
          },
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          {/* Close (Cancel) Button and Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              onClick={handleCloseDrawer}
              size="small"
              sx={{
                '&:hover': { backgroundColor: '#E8EBF1' },
                transition: 'all 0.2s ease',
              }}
            >
              <X size={18} />
            </IconButton>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 18,
                color: 'custom.textBlue',
                letterSpacing: '-0.2px',
              }}
            >
              {store.friendly_name} Menu Configuration
            </Typography>
          </Box>

          {isEditing || !store.is_menu_configured ? (
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={
                !store ||
                isSaving ||
                isSubmitting ||
                (menuConfig.data.posName === 'Urban Piper' &&
                  (!menuConfig.isAccessRequested ||
                    !menuConfig.data.menuSettingDetails?.[store.id]?.refId)) ||
                (menuConfig.data.posName === 'tmbill' &&
                  (!menuConfig.data.menuSettingDetails?.[store.id]
                    ?.tmBillBrandShortName ||
                    !menuConfig.isMenuGenerationStarted))
              }
              sx={{
                fontSize: 14,
                fontWeight: 500,
                borderRadius: '8px',
                textTransform: 'none',
                boxShadow: 'none',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                },
              }}
              onClick={handleSave}
            >
              {isSubmitting || isSaving ? 'Saving...' : 'Save'}
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
          )}
        </Box>

        <Divider
          sx={{
            borderColor: 'rgba(0, 0, 0, 0.08)',
            mb: 1,
            mx: -3,
          }}
        />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose how you'd like to add your menu to Quibble AI
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '16px',
            mb: 3,
          }}
        >
          {methods.map(method => (
            <Box
              key={method.id}
              onClick={() => {
                if (
                  !(
                    posIntegrationData?.data?.data?.length > 0 &&
                    !isEditing &&
                    store.is_menu_configured
                  )
                ) {
                  handleMethodSelect(method.id);
                }
              }}
              sx={{
                width: '221px',
                height: '157px',
                padding: '16px',
                border: '1px solid',
                borderColor:
                  menuConfig.data.menuSettingMethod === method.id
                    ? 'primary.main'
                    : 'grey.300',
                borderRadius: '12px',
                cursor:
                  posIntegrationData?.data?.data?.length > 0 &&
                  !isEditing &&
                  store.is_menu_configured
                    ? 'not-allowed'
                    : 'pointer',
                position: 'relative',
                bgcolor: 'background.paper',
                '&:hover': { borderColor: 'primary.main' },
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {method.highlight && (
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                  {method.highlight}
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    color: 'primary.main',
                    bgcolor: '#F4F7FF',
                    p: 0.5,
                    height: '44px',
                    width: '44px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {method.icon}
                </Box>
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 500, fontSize: '14px' }}
                  >
                    {method.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: '12px' }}
                  >
                    {method.description}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
        <Divider sx={{ mb: 2 }} />

        {menuConfig.data.menuSettingMethod === 'pos' && (
          <>
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ width: '120px', flexShrink: 0 }}
                >
                  POS System
                </Typography>
                <TextField
                  select
                  fullWidth
                  name="posName"
                  value={menuConfig.data.posName || ''}
                  onChange={handleChange}
                  size="small"
                  disabled={
                    posIntegrationData?.data?.data?.length > 0 &&
                    !isEditing &&
                    store.is_menu_configured
                  }
                  error={!!errors.posName}
                  helperText={errors.posName}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                    },
                  }}
                >
                  {posConfig?.data?.data?.map((pos: any) => (
                    <MenuItem key={pos._id} value={pos.posName}>
                      {pos.posName}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {menuConfig.data.posName === 'Urban Piper' && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ width: '120px', flexShrink: 0 }}
                    >
                      Environment
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      name="environment"
                      value={menuConfig.data.environment || 'stage'}
                      onChange={handleChange}
                      size="small"
                      disabled={
                        posIntegrationData?.data?.data?.length > 0 &&
                        !isEditing &&
                        store.is_menu_configured
                      }
                      error={!!errors.environment}
                      helperText={errors.environment}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '6px',
                        },
                      }}
                    >
                      {posConfig?.data?.data?.find(
                        (pos: any) => pos.posName === menuConfig.data.posName
                      )?.pos_config[0] &&
                        Object.keys(
                          posConfig.data.data.find(
                            (pos: any) =>
                              pos.posName === menuConfig.data.posName
                          ).pos_config[0]
                        ).map((env: string) => (
                          <MenuItem key={env} value={env}>
                            {env.charAt(0).toUpperCase() + env.slice(1)}
                          </MenuItem>
                        ))}
                    </TextField>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ width: '120px', flexShrink: 0 }}
                    >
                      Business ID
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          fullWidth
                          placeholder="Enter your POS Business ID"
                          name="businessId"
                          size="small"
                          disabled={
                            posIntegrationData?.data?.data?.length > 0 &&
                            !isEditing &&
                            store.is_menu_configured
                          }
                          value={menuConfig.data.businessId}
                          onChange={handleChange}
                          error={!!errors.businessId}
                          helperText={errors.businessId}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '6px',
                              fontSize: '12px',
                            },
                            '& > div': {
                              height: '40px',
                            },
                          }}
                        />
                        <Button
                          variant="outlined"
                          color="primary"
                          disabled={
                            menuConfig.data.businessId === '' ||
                            isConnectingBusiness
                          }
                          onClick={handleRequestAccess}
                          sx={{
                            minWidth: 136,
                            height: 40,
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontSize: '14px',
                            boxShadow: 'none',
                            whiteSpace: 'nowrap',
                            ...(menuConfig.isAccessRequested && {
                              bgcolor: 'white',
                              border: '1px solid',
                              borderColor: 'rgba(55, 183, 125, 1)',
                              color: 'rgba(55, 183, 125, 1)',
                              fontSize: '14px',
                              pointerEvents: 'none',
                              '&:hover': {
                                bgcolor: 'white',
                              },
                            }),
                          }}
                        >
                          {menuConfig.isAccessRequested ? (
                            <>
                              <CheckCircle
                                size={16}
                                weight="fill"
                                color="rgba(55, 183, 125, 1)"
                                style={{ marginRight: 2 }}
                              />
                              Request Sent
                            </>
                          ) : isConnectingBusiness ? (
                            'Requesting...'
                          ) : (
                            'Request Access'
                          )}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}

              {menuConfig.data.posName === 'tmbill' && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ width: '120px', flexShrink: 0 }}
                  >
                    TM POS ID
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      placeholder="Enter your TM POS ID"
                      name="tmbillposId"
                      size="small"
                      value={menuConfig.data.tmbillposId}
                      onChange={handleChange}
                      disabled={
                        posIntegrationData?.data?.data?.length > 0 &&
                        !isEditing &&
                        store.is_menu_configured
                      }
                      error={!!errors.tmbillposId}
                      helperText={errors.tmbillposId}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '6px',
                          fontSize: '12px',
                        },
                        '& > div': {
                          height: '40px',
                        },
                      }}
                    />
                  </Box>
                </Box>
              )}

              {menuConfig.isAccessRequested &&
                menuConfig.data.posName === 'Urban Piper' && (
                  <Box
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ width: '120px', flexShrink: 0, mt: 1 }}
                    >
                      Location ID
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          placeholder="Store Alias"
                          size="small"
                          value={store?.friendly_name}
                          disabled
                          sx={{ width: '50%' }}
                        />
                        <TextField
                          placeholder="ID"
                          size="small"
                          name={`locationId.${store.id}`}
                          disabled={
                            posIntegrationData?.data?.data?.length > 0 &&
                            !isEditing &&
                            store.is_menu_configured
                          }
                          value={
                            menuConfig.data.menuSettingDetails?.[store.id]
                              ?.refId || ''
                          }
                          onChange={e => {
                            dispatch(
                              updateMenuConfig({
                                menuSettingDetails: {
                                  [store.id]: {
                                    storeId: store.id,
                                    refId: e.target.value,
                                  },
                                },
                              })
                            );
                            dispatch(setHasUnsavedChanges(true));
                          }}
                          error={!!errors[`locationId.${store.id}`]}
                          helperText={errors[`locationId.${store.id}`]}
                          sx={{ width: '50%' }}
                        />
                      </Box>
                    </Box>
                  </Box>
                )}

              {menuConfig.data.posName === 'tmbill' &&
                menuConfig.data.tmbillposId && (
                  <Box
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ width: '120px', flexShrink: 0, mt: 1 }}
                    >
                      Brand Short Name
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{ display: 'flex', gap: 2, alignItems: 'center' }}
                      >
                        <TextField
                          placeholder="Store Alias"
                          size="small"
                          value={store?.friendly_name}
                          disabled
                          sx={{ width: '40%' }}
                        />
                        <TextField
                          placeholder="brand short name"
                          size="small"
                          value={
                            menuConfig.data.menuSettingDetails?.[store.id]
                              ?.tmBillBrandShortName || ''
                          }
                          disabled={
                            posIntegrationData?.data?.data?.length > 0 &&
                            !isEditing &&
                            store.is_menu_configured
                          }
                          onChange={e => {
                            dispatch(
                              updateMenuConfig({
                                menuSettingDetails: {
                                  [store.id]: {
                                    storeId: store.id,
                                    tmBillBrandShortName: e.target.value,
                                  },
                                },
                              })
                            );
                            dispatch(setHasUnsavedChanges(true));
                          }}
                          error={!!errors[`tmBillBrandShortName.${store.id}`]}
                          helperText={
                            errors[`tmBillBrandShortName.${store.id}`]
                          }
                          sx={{
                            width: '40%',
                          }}
                        />
                        <Button
                          variant="text"
                          size="small"
                          onClick={handlePreviewInfo}
                          disabled={
                            !menuConfig.data.menuSettingDetails?.[store.id]
                              ?.tmBillBrandShortName
                          }
                          sx={{
                            color: 'primary.main',
                            fontSize: '12px',
                            fontWeight: 500,
                          }}
                        >
                          preview info
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                )}
            </Box>

            <Box sx={{ mt: 'auto' }}>
              <Divider sx={{ mb: 2 }} />
              <List>
                <ListItem sx={{ px: 0 }}>
                  <ListItemText
                    primary="How to Connect Your POS with Quibble AI"
                    secondary={
                      <Box component="ol" sx={{ pl: 2, mt: 1 }}>
                        <li>Enter Your Business ID</li>
                        <li>Request Integration Access</li>
                        <li>Enter Your Location ID</li>
                        <li>Proceed to the Next Step</li>
                        <li>Publish Your Menu</li>
                      </Box>
                    }
                  />
                </ListItem>
              </List>
            </Box>
          </>
        )}

        <AccessRequestSuccessModal
          open={showAccessModal}
          onClose={handleCloseAccessModal}
        />
        <TMBillBrandInfoModal
          open={showBrandInfoModal}
          onClose={() => setShowBrandInfoModal(false)}
          storeInfo={selectedStoreInfo}
          isLoading={isBrandInfoLoading}
          handleMenuGeneration={handleMenuGeneration}
          isSubmitting={isSubmitting}
        />
      </Drawer>

      {/* Confirmation Modal */}
      <ConfirmationModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleSave}
        isPending={isSaving || isSubmitting}
        title="Discard Changes"
        description="You have not completed the menu configuration. Please Save the Menu Configuration Details first to see the menu"
        confirmButtonText="Save"
        cancelButtonText="Cancel"
        confirmButtonColor="success"
      />
      <MessagePopup alert={alert} setAlert={setAlert} />
    </>
  );
};

export default MenuConfigurationDrawer;
